import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import type { Crumb, Field, PageItemRow } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { ATTACHMENTS } from "../../lib/attach.ts";
import { carriedValues } from "../../lib/backrefs.ts";
import { mailText } from "../../lib/emailText.ts";
import { figuresOf } from "../../lib/fieldTypes.ts";
import { type FileType, fileType } from "../../lib/fileTypes.ts";
import { formatInstant, formatRelative } from "../../lib/format.ts";
import { spaceUrl } from "../../lib/paths.ts";
import { fileNameOf, titleOf } from "../../lib/titles.ts";
import { InlineButton, Link } from "../../ui/Button.tsx";
import { IssueBadges, ItemCommentCount, RefCount, ThreadCount } from "../../ui/counts.tsx";
import { CollapseIcon, CopiesIcon, ExpandIcon } from "../../ui/icons.tsx";
import { LINK } from "../../ui/styles.ts";
import { Breadcrumb } from "../folders/breadcrumb.tsx";
import { dragIds } from "../folders/drop.ts";
import { VisibilityMark } from "../item/visibility.tsx";
import { EntityLabel } from "./entityLabel.tsx";
import { AttachmentCount, PeekKindIcon } from "./icons.tsx";
import { AttachedTo, type ItemListProps, UnlinkButton } from "./rows.tsx";
import { Value } from "./values.tsx";

/** Fields a card shows before "Show more". */
const CARD_FIELDS = 3;

type Row = PageItemRow["data"][number];

/** A card's "label … value" line: the label up to 40% of it, the value at least 60% (both wrap). */
function Line({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="flex items-baseline justify-between gap-4">
      <dt class="max-w-[40%] shrink-0 text-xs text-zinc-500 [overflow-wrap:anywhere]">{label}</dt>
      <dd class="min-w-0 flex-1 text-right [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

/** Whether a field has something to show (cards leave empty ones out). */
function filled(f: Field, row: Row): boolean {
  if (f.type === "ref") {
    return (row.refs?.[f.key]?.length ?? 0) > 0;
  }
  const v = row.data?.[f.key];
  return v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
}

type CardProps = ItemListProps & { row: Row };

/** What a board adds to a card. */
interface BoardCard {
  /** The field the board groups by: its column says it already. */
  hide: string;
  /** In how many other columns the item also is (a multiple-choice field): drawn dashed, with a marker. */
  copies: number;
  /** Highlighted: the pointer is over one of its copies. */
  lit: boolean;
  onHover: (id: string | null) => void;
  /** Can be dragged to another column. */
  draggable: boolean;
  /** Where it is, when its column doesn't say it. */
  path?: readonly Crumb[] | undefined;
}

/** The card's look: dashed when it's one of several copies, ringed when lit. */
function frame(board: BoardCard | undefined): string {
  const edge = board && board.copies > 0 ? "border-dashed border-zinc-400" : "border-zinc-200";
  const lit = board?.lit ? " ring-2 ring-blue-400" : "";
  const grab = board?.draggable ? " cursor-grab active:cursor-grabbing" : "";
  return `${edge}${lit}${grab}`;
}

/**
 * An item as a card: its picture, kind, title and issues on top, its first few fields (the rest behind a toggle),
 * dates below. On a board, also its copies in other columns and dragging.
 */
/** Files whose thumbnail is a page of them (a document's, a spreadsheet's cells, a PDF's or presentation's first
 *  page: thumbs.rs), shown from their top left on white; a photo is cropped about its middle. */
const PAGED = new Set<FileType>([
  "revisa-doc",
  "revisa-sheet",
  "pdf",
  "word",
  "text-document",
  "pages",
  "excel",
  "spreadsheet",
  "numbers",
  "presentation",
  "keynote",
]);

function pagePicture(row: {
  file?: { mime: string; name?: string | null | undefined } | null | undefined;
  title?: string | undefined;
}): boolean {
  const type = row.file ? fileType(row.file.mime, fileNameOf(row)) : null;
  return type !== null && PAGED.has(type);
}

export function ItemCard({
  space,
  row,
  entity,
  entities,
  members,
  board,
  hide,
  link,
  conversations,
  onRemove,
  draggable,
}: CardProps & { board?: BoardCard }) {
  const [open, setOpen] = useState(false);
  // The fields shown on the table first (they're what the table shows), then the rest.
  const fields = (entity?.fields ?? [])
    .filter((f) => f.key !== board?.hide && f.key !== hide && filled(f, row))
    .sort((a, b) => Number(b.show_on_table ?? false) - Number(a.show_on_table ?? false));
  const carried = link ? carriedValues(row, link) : {};
  const shown = open ? fields : fields.slice(0, CARD_FIELDS);
  const more = fields.length - CARD_FIELDS;
  const of = entity ? undefined : entities.find((e) => e.id === row.entity);
  const attachments = row.refs?.[ATTACHMENTS]?.length ?? 0;
  const footer = [
    t("items.cardUpdated", { when: formatRelative(row.updated_at) }),
    row.reviewed_at ? t("items.cardReviewed", { when: formatRelative(row.reviewed_at) }) : null,
  ];
  return (
    <article
      class={`flex flex-col overflow-hidden rounded-lg border bg-white ${frame(board)}`}
      draggable={board?.draggable ?? draggable}
      onDragStart={(e) => dragIds(e, [row.id], row.folder ?? null)}
      onMouseEnter={board && board.copies > 0 ? () => board.onHover(row.id) : undefined}
      onMouseLeave={board && board.copies > 0 ? () => board.onHover(null) : undefined}
    >
      <div class="flex flex-1 flex-col p-4">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <span class="flex items-start gap-2">
              <span class="mt-0.5 shrink-0">
                <PeekKindIcon space={space} item={row} />
              </span>
              <span class="min-w-0 [overflow-wrap:anywhere]">
                <Link href={spaceUrl(space, `/items/${row.id}`)} draggable={false} class={`${LINK} font-medium`}>
                  {titleOf(row)}
                </Link>
                {conversations ? <ThreadCount n={row.thread_count} /> : null}
              </span>
              <VisibilityMark item={row} members={members} />
            </span>
            {board?.path && board.path.length > 0 ? (
              <Breadcrumb space={space} path={board.path} small={true} class="mt-0.5" />
            ) : null}
            {row.attached_to ? (
              <div class="line-clamp-2 [overflow-wrap:anywhere]">
                <AttachedTo space={space} parent={row.attached_to} />
              </div>
            ) : null}
          </div>
          <span class="flex shrink-0 items-center gap-1">
            <IssueBadges counts={row.issues} />
            <ItemCommentCount space={space} id={row.id} />
            {board && board.copies > 0 ? <Copies n={board.copies} /> : null}
            {onRemove ? <UnlinkButton title={titleOf(row)} onClick={() => onRemove(row.id)} /> : null}
          </span>
        </div>
        <dl class="mt-3 flex flex-col gap-1 text-sm">
          {(link?.fields ?? [])
            .filter((f) => carried[f.key] !== undefined)
            .map((f) => (
              <Line key={`link.${f.key}`} label={f.label || f.key}>
                <span class={figuresOf(f.type)}>
                  <Value field={f} value={carried[f.key]} region={space.region} />
                </span>
              </Line>
            ))}
          {row.kind === "email" ? (
            <>
              <Line label={t("email.from")}>{mailText(row.data?.["from"])}</Line>
              <Line label={t("email.to")}>{mailText(row.data?.["to"])}</Line>
            </>
          ) : null}
          {of ? (
            <Line label={t("items.entity")}>
              <EntityLabel entity={of} large={true} />
            </Line>
          ) : null}
          {shown.map((f) => (
            <Line key={f.key} label={f.label || f.key}>
              <span class={figuresOf(f.type)}>
                {f.type === "ref" ? (
                  <RefCount n={row.refs?.[f.key]?.length ?? 0} />
                ) : (
                  <Value field={f} value={row.data?.[f.key]} region={space.region} />
                )}
              </span>
            </Line>
          ))}
          {attachments > 0 ? (
            <Line label={t("items.attachments")}>
              <AttachmentCount n={attachments} />
            </Line>
          ) : null}
        </dl>
        {more > 0 ? (
          <InlineButton
            size="xs"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            class="mt-1 self-start"
            icon={open ? <CollapseIcon size={12} /> : <ExpandIcon size={12} />}
          >
            {open ? t("items.fewerFields") : t("items.moreFields", { count: more })}
          </InlineButton>
        ) : null}
        <p class="mt-auto pt-3 text-xs text-zinc-500" title={formatInstant(row.updated_at)}>
          {footer.filter(Boolean).join(" · ")}
        </p>
      </div>
      {/* The picture last, full width: every card's title starts at its top, lining up along the row. */}
      {row.thumb ? (
        <img
          src={row.thumb}
          alt=""
          width="320"
          height="180"
          loading="lazy"
          draggable={false}
          class={`aspect-video w-full border-t border-zinc-200 bg-zinc-100 object-cover ${pagePicture(row) ? "object-left-top bg-white" : ""}`}
        />
      ) : null}
    </article>
  );
}

/** "Also in 2 other columns": a multiple-choice item's mark on each of its copies. */
function Copies({ n }: { n: number }) {
  const words = t("items.board.alsoIn", { count: n });
  return (
    <span title={words} class="flex items-center gap-0.5 text-xs text-zinc-500">
      <CopiesIcon size={14} />
      <span aria-hidden="true">{`+${n}`}</span>
      <span class="sr-only">{words}</span>
    </span>
  );
}

/** Items as cards: as many columns as fit (one on phones, four or more on very wide screens). */
export function ItemCards({ page, ...p }: ItemListProps & { page: PageItemRow }) {
  return (
    // Each card as tall as it is, at the top of its row (not stretched to the row's tallest).
    <div class="grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] items-start gap-3">
      {page.data.map((row) => (
        <ItemCard key={row.id} {...p} row={row} />
      ))}
    </div>
  );
}
